/**
 * Typed fetch for `/api` (docs/08 §7): cookies included, errors normalised to `ApiError`,
 * `Idempotency-Key` on every mutation (the offline outbox replays them safely), `api:unauthorized`
 * on 401 (the auth layer goes back to /login), Blob downloads for PDF, Excel and stored documents.
 */
import type { ApiErrorBody } from 'core';
import { uuid } from './utils';

export const API_BASE = '/api';

/** Dispatched on `window` when the API answers 401 (the auth layer redirects to /login). */
export const UNAUTHORIZED_EVENT = 'api:unauthorized';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** No response at all (offline, DNS, timeout). */
  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export interface ApiRequestOptions {
  method?: HttpMethod;
  /** JSON body. */
  body?: unknown;
  /** Multipart body (uploads); takes precedence over `body`. */
  formData?: FormData;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Reuse a key when replaying a queued mutation; generated otherwise. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export function buildUrl(path: string, query?: ApiRequestOptions['query']): string {
  const url = path.startsWith('/api')
    ? path
    : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

function isErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as ApiErrorBody).error?.code === 'string'
  );
}

/** Turns a non-2xx response into an `ApiError` (envelope of docs/08 §3 when present). */
export async function toApiError(response: Response): Promise<ApiError> {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    // not JSON (proxy error page, empty body)
  }
  if (isErrorBody(payload)) {
    return new ApiError(
      response.status,
      payload.error.code,
      payload.error.message,
      payload.error.details,
    );
  }
  const fallback =
    response.status >= 500
      ? 'Servizio non disponibile, riprovare tra poco'
      : `Errore ${response.status}`;
  return new ApiError(response.status, 'HTTP_ERROR', fallback);
}

/** Messages for the error codes the user can act on (the server message is used otherwise). */
const NETWORK_MESSAGE = 'Connessione assente o server non raggiungibile';

/** Italian message for any error thrown by the API layer (or anything else). */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.isNetworkError) return NETWORK_MESSAGE;
    if (err.code === 'RATE_LIMITED')
      return err.message || 'Troppi tentativi: riprova tra qualche minuto';
    return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Si è verificato un errore imprevisto';
}

/** Filename of a `Content-Disposition` header (RFC 5987 `filename*` preferred). */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
    } catch {
      // malformed escape: fall back to the plain parameter
    }
  }
  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/.exec(header);
  const value = plain?.[2] ?? plain?.[1];
  return value ? value.trim() : null;
}

/**
 * Copy of a multipart body whose files are in-memory Blobs. Safari/iOS 26.5+ (WebKit bug 319985)
 * sends a disk-backed `File` (chosen with `<input type=file>`, or read back from IndexedDB) as an
 * empty body when the page is controlled by a service worker, as this PWA is; the server then
 * answers «Nessun file ricevuto». Reading the bytes in the page works, so every file is sent as a
 * fresh Blob of those bytes, with its name and type. Text fields are copied as they are.
 */
export async function inMemoryFormData(formData: FormData): Promise<FormData> {
  const parts: [string, FormDataEntryValue][] = [];
  formData.forEach((value, field) => parts.push([field, value]));
  const copy = new FormData();
  for (const [field, value] of parts) {
    if (typeof value === 'string') {
      copy.append(field, value);
      continue;
    }
    const bytes = await readFileBytes(value);
    copy.append(field, new Blob([bytes], { type: value.type }), value.name);
  }
  return copy;
}

const fileUnreadable = () =>
  new ApiError(
    400,
    'FILE_UNREADABLE',
    'Il browser non riesce a leggere il file: rifai la foto o scegli di nuovo il file',
  );

/**
 * Bytes of a Blob read in the page. A read the browser refuses (a File no longer readable: moved,
 * replaced, or an IndexedDB blob WebKit cannot open), or that returns nothing for a non-empty
 * file, is a 400 `FILE_UNREADABLE` with an Italian message: non-transient, shown to the user.
 */
export async function readFileBytes(value: Blob): Promise<ArrayBuffer> {
  let bytes: ArrayBuffer;
  try {
    bytes = await value.arrayBuffer();
  } catch {
    throw fileUnreadable();
  }
  if (bytes.byteLength === 0 && value.size > 0) throw fileUnreadable();
  return bytes;
}

async function send(path: string, options: ApiRequestOptions): Promise<Response> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
  let body: BodyInit | undefined;
  if (options.formData) {
    body = await inMemoryFormData(options.formData);
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }
  if (method !== 'GET') headers['Idempotency-Key'] = options.idempotencyKey ?? uuid();

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      body,
      credentials: 'include',
      signal: options.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'NETWORK_ERROR', NETWORK_MESSAGE);
  }

  if (!response.ok) {
    const error = await toApiError(response);
    if (response.status === 401 && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT, { detail: error }));
    }
    throw error;
  }
  return response;
}

export async function apiFetch<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const response = await send(path, options);
  if (response.status === 204) return undefined as T;
  const type = response.headers.get('content-type') ?? '';
  return (type.includes('application/json') ? await response.json() : await response.blob()) as T;
}

export interface ApiFile {
  blob: Blob;
  /** From `Content-Disposition`; null when the server sends none. */
  filename: string | null;
}

/** GET of a file (PDF, Excel, stored document) as a Blob, with errors normalised like `apiFetch`. */
export async function apiBlob(
  path: string,
  options: Omit<ApiRequestOptions, 'body' | 'formData'> = {},
): Promise<ApiFile> {
  const response = await send(path, { ...options, headers: { Accept: '*/*', ...options.headers } });
  return {
    blob: await response.blob(),
    filename: filenameFromDisposition(response.headers.get('content-disposition')),
  };
}

/** Downloads a file from the API and saves it with the server filename (or `fallbackName`). */
export async function downloadFile(
  path: string,
  fallbackName: string,
  options?: Omit<ApiRequestOptions, 'body' | 'formData'>,
): Promise<void> {
  const { blob, filename } = await apiBlob(path, options);
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename ?? fallbackName;
    a.rel = 'noopener';
    document.body.append(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}

/**
 * Opens a file of the API (e.g. a planimetry or a signed document) in a new tab. The tab is opened
 * synchronously, before the download, so that Safari does not block it as a pop-up.
 */
export async function openFile(path: string): Promise<void> {
  const win = window.open('', '_blank');
  try {
    const { blob } = await apiBlob(path);
    const url = URL.createObjectURL(blob);
    if (win) win.location.href = url;
    else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);
  } catch (err) {
    win?.close();
    throw err;
  }
}

export const api = {
  get: <T>(path: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
  upload: <T>(
    path: string,
    formData: FormData,
    options?: Omit<ApiRequestOptions, 'method' | 'formData'>,
  ) => apiFetch<T>(path, { ...options, method: 'POST', formData }),
  blob: apiBlob,
  download: downloadFile,
  open: openFile,
};
